import { apis } from '@/config/apiConfig'
import { createMutationCall } from '@/queries/utils/api-helpers'
import {
  DEFAULT_LATITUDE,
  DEFAULT_LONGITUDE,
  DEFAULT_ZOOM,
} from '@/containers/Map/Viewport/Constants'
import { usersKeys } from './use-users'
import {
  useQuery,
  useMutation,
  useQueryClient,
  UseQueryResult,
  UseMutationResult,
  UseQueryOptions,
} from '@tanstack/react-query'
import {
  UserData,
  UserInitResponse,
  UserThirdPartyTokens,
  UserTokensApiResponse,
  UserUpdatePayload,
} from '@/types/user'
import { apiFetch } from '@/utilities/api'
import { verifyResult } from '@/utilities/utils'
import { objectSort } from '@/utilities/util'
import { Auth } from 'aws-amplify'

/** Prefix for all per-user third-party token queries (Released / HubSpot). Use with `removeQueries` on logout. */
export const USER_TOKEN_QUERIES_PREFIX = ['user', 'tokens'] as const

/** At most one refetch per hour while the query is active (TanStack: stale until this elapses). */
const USER_TOKENS_STALE_MS = 1000 * 60 * 60 // 1 hour
/** Keep token cache a bit longer than stale window so inactive tabs can reuse without immediate GC. */
const USER_TOKENS_GC_MS = 1000 * 60 * 90 // 90 minutes

// Default user state
export const defaultUser = (): UserData => ({
  UUID: '',
  isAdmin: false,
  isActive: false,
  canAccessQA: false,
  role: undefined,
  permissions: undefined,
  adminAccounts: [],
  profile: undefined,
  accounts: [],
  preferences: undefined,
  accountID: 0,
  mapID: 0,
  isLoggedIn: false,
})

// Query key factory
export const userKeys = {
  all: ['user'] as const,
  init: () => [...userKeys.all, 'init'] as const,
  tokens: (uuid: string) => [...USER_TOKEN_QUERIES_PREFIX, uuid] as const,
  preferences: () => [...userKeys.all, 'preferences'] as const,
  session: () => [...userKeys.all, 'session'] as const,
}

// Session check hook using React Query
export const useUserSession = () => {
  const queryClient = useQueryClient()

  return useQuery({
    queryKey: userKeys.session(),
    queryFn: async () => {
      try {
        // Check cache first for performance
        const cachedUser = queryClient.getQueryData<UserInitResponse>(
          userKeys.init()
        )
        if (cachedUser?.success && cachedUser?.data?.UUID) {
          return { hasSession: true, isChecking: false }
        }

        // Check AWS session
        await Auth.currentSession()
        return { hasSession: true, isChecking: false }
      } catch {
        return { hasSession: false, isChecking: false }
      }
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 10, // 10 minutes
    refetchOnWindowFocus: false,
    retry: false, // Don't retry session checks
  })
}

// user fetch function and data setup
export const fetchUserInit = async (): Promise<UserInitResponse> => {
  return new Promise((resolve, reject) => {
    apiFetch(`${apis.apiDatabase.uri}user/init`, 'GET', null, result => {
      if (result.success) {
        resolve(result as UserInitResponse)
      } else {
        reject(new Error(result.message ?? 'Failed to fetch user data.'))
      }
    })
  })
}

export const fetchUserTokens = async (): Promise<UserTokensApiResponse> => {
  return new Promise((resolve, reject) => {
    apiFetch(`${apis.apiDatabase.uri}user/tokens`, 'GET', null, result => {
      if (result.success) {
        resolve(result as UserTokensApiResponse)
      } else {
        reject(new Error(result.message ?? 'Failed to fetch tokens.'))
      }
    })
  })
}

export const useUserTokens = (
  uuid: string | undefined
): UseQueryResult<UserThirdPartyTokens, Error> => {
  const { data: sessionData, isLoading: isSessionLoading } = useUserSession()

  return useQuery<UserTokensApiResponse, Error, UserThirdPartyTokens>({
    queryKey: userKeys.tokens(uuid ?? ''),
    queryFn: fetchUserTokens,
    staleTime: USER_TOKENS_STALE_MS,
    gcTime: USER_TOKENS_GC_MS,
    enabled:
      !!uuid &&
      !isSessionLoading &&
      sessionData?.hasSession === true &&
      uuid.length > 0,
    select: data => {
      if (data.success && data.data) {
        return {
          releasedToken: data.data.releasedToken ?? null,
          hubspotVisitorToken: data.data.hubspotVisitorToken ?? null,
        }
      }
      return {
        releasedToken: null,
        hubspotVisitorToken: null,
      }
    },
  })
}

const verifyUserData = (
  data: UserInitResponse
): data is UserInitResponse & { data: UserData } => {
  return verifyResult(data) && data.success && !!data.data
}

// Main user query hook
export const useUser = (
  options?: UseQueryOptions<UserInitResponse, Error, UserData>
): UseQueryResult<UserData, Error> => {
  const { data: sessionData, isLoading: isSessionLoading } = useUserSession()

  const queryResult = useQuery<UserInitResponse, Error, UserData>({
    queryKey: userKeys.init(),
    queryFn: fetchUserInit,
    staleTime: 1000 * 60 * 5, // 5 minutes - back to original
    gcTime: 1000 * 60 * 10, // 10 minutes - back to original
    enabled:
      (options?.enabled ?? true) &&
      !isSessionLoading &&
      sessionData?.hasSession,
    select: data => {
      if (verifyUserData(data)) {
        const {
          profile,
          isAdmin,
          isActive,
          canAccessQA,
          role,
          permissions,
          adminAccounts,
          accounts = [],
          preferences,
          accountID,
          mapID,
        } = data.data

        const sortedAccounts = accounts.sort(objectSort('name'))

        const user = defaultUser()
        user.UUID = data.userUUID ?? ''

        // Handle account selection and validation
        if (!accounts.length) {
          throw new Error(
            'You are not a part of any accounts. Please contact support@myassetmap.com.'
          )
        }

        // Validate first account has an ID if we need to use it as fallback
        const firstValidAccount = accounts.find(acc => acc.id != null)
        if (!firstValidAccount) {
          throw new Error(
            'Invalid account data. Please contact support@myassetmap.com.'
          )
        }

        // Set accountID with fallback to first valid account if none specified
        user.accountID = accountID ?? firstValidAccount.id
        const account = accounts.find(acc => acc.id === user.accountID)

        if (!account) {
          throw new Error(
            'No active accounts have been found. Please have your admin add to you an account.'
          )
        }

        // Set user permissions and role based on account
        user.isAdmin =
          isAdmin || adminAccounts?.includes(user.accountID) || false
        user.isActive = isActive
        user.canAccessQA = canAccessQA ?? false
        user.role = role
        user.permissions = permissions
        user.adminAccounts = adminAccounts
        user.accounts = sortedAccounts

        // Handle map selection with fallbacks
        user.mapID = mapID ?? account.maps?.[0]?.id ?? null

        // Set remaining user data
        user.profile = profile
        user.preferences = preferences

        // Validate required data exists
        if (!user.UUID || !user.accountID || !user.mapID) {
          throw new Error(
            'Missing required user data. Please contact support@myassetmap.com.'
          )
        }

        user.isLoggedIn = true
        return user
      }
      return defaultUser()
    },
    ...options,
    refetchOnWindowFocus: false,
  })

  return queryResult
}
// Update user mutation hook
export const useUpdateUser = (): UseMutationResult<
  unknown,
  Error,
  UserUpdatePayload
> => {
  const queryClient = useQueryClient()

  return useMutation<unknown, Error, UserUpdatePayload>({
    mutationFn: (updates: UserUpdatePayload) =>
      createMutationCall(`${apis.apiDatabase.uri}user/update`, 'POST', updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.init() })
      queryClient.invalidateQueries({ queryKey: usersKeys.all })
    },
  })
}

// Helper function
export const getMapHomeLocation = (user: UserData) => {
  const accountId = user?.accountID
  const mapId = user?.mapID
  const activeAccount = user?.accounts?.find(
    account => account.id === accountId
  )

  if (!activeAccount) {
    return { x: DEFAULT_LATITUDE, y: DEFAULT_LONGITUDE, z: DEFAULT_ZOOM }
  }

  const activeMap = activeAccount?.maps?.find(m => m.id === mapId)

  return (
    activeMap?.location ?? {
      x: DEFAULT_LATITUDE,
      y: DEFAULT_LONGITUDE,
      z: DEFAULT_ZOOM,
    }
  )
}

export const getActiveMap = (user: UserData) => {
  const accountId = user?.accountID
  const mapId = user?.mapID
  const activeAccount = user?.accounts?.find(
    account => account.id === accountId
  )
  return activeAccount?.maps?.find(m => m.id === mapId)
}
